import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { PrismicRichText } from "@prismicio/react";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, Configure, useInstantSearch } from "react-instantsearch";

const searchClient = algoliasearch(
  "G0VLPZK1K5",
  "1d5ab2118d90a0ca1794dc8c22845f51"
);

// Custom component to filter past events client-side with pagination
function PastEventsHits({ hitComponent }) {
  const { results } = useInstantSearch();
  const [currentPage, setCurrentPage] = React.useState(0);
  const eventsPerPage = 9;
  
  // Get today's timestamp for filtering past events
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayValue = Math.floor(today.valueOf() / 1000);
  
  // Filter for past events client-side since date_timestamp might not be in index yet
  const pastEvents = results?.hits?.filter(event => {
    // Try to parse date from date_published format "MMMM DD, YYYY"
    if (event.data.date_published) {
      const eventDate = new Date(event.data.date_published);
      const eventTimestamp = Math.floor(eventDate.valueOf() / 1000);
      return eventTimestamp < todayValue;
    }
    return false;
  }) || [];

  // Calculate pagination
  const totalPages = Math.ceil(pastEvents.length / eventsPerPage);
  const startIndex = currentPage * eventsPerPage;
  const endIndex = startIndex + eventsPerPage;
  const currentEvents = pastEvents.slice(startIndex, endIndex);

  if (pastEvents.length === 0) {
    return (
      <p className="text-lg flex items-center gap-4 px-5 py-2 bg-grey-light w-fit rounded-xl">
        <i className="fa-regular fa-calendar-xmark text-2xl text-red" />
        <span>No past events available.</span>
      </p>
    );
  }

  return (
    <div>
      <div className="ais-Hits">
        <ol className="ais-Hits-list">
          {currentEvents.map((hit, index) => {
            const HitComponent = hitComponent;
            return (
              <li key={hit.objectID || index} className="ais-Hits-item">
                <HitComponent hit={hit} />
              </li>
            );
          })}
        </ol>
      </div>
      
      {/* Custom Pagination matching original style */}
      {totalPages > 1 && (
        <nav className="ais-Pagination font-bold text-xl" style={{padding: '1rem 0'}}>
          <ul className="ais-Pagination-list">
            <li className={`ais-Pagination-item ais-Pagination-item--previousPage ${currentPage === 0 ? 'ais-Pagination-item--disabled' : ''}`}>
              <a 
                className={`ais-Pagination-link ${currentPage === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={currentPage > 0 ? () => setCurrentPage(currentPage - 1) : undefined}
              >
                ‹
              </a>
            </li>
            
            {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i;
              } else {
                // Show current page and one on each side when possible
                if (currentPage === 0) {
                  pageNum = i; // Show 0, 1, 2
                } else if (currentPage === totalPages - 1) {
                  pageNum = totalPages - 3 + i; // Show last 3 pages
                } else {
                  pageNum = currentPage - 1 + i; // Show current-1, current, current+1
                }
              }
              
              return (
                <li key={pageNum} className={`ais-Pagination-item ais-Pagination-item--page ${pageNum === currentPage ? 'ais-Pagination-item--selected' : ''}`}>
                  <a 
                    className={`ais-Pagination-link ${pageNum === currentPage ? 'bg-accessible-green text-white rounded' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum + 1}
                  </a>
                </li>
              );
            })}
            
            <li className={`ais-Pagination-item ais-Pagination-item--nextPage ${currentPage === totalPages - 1 ? 'ais-Pagination-item--disabled' : ''}`}>
              <a 
                className={`ais-Pagination-link ${currentPage === totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={currentPage < totalPages - 1 ? () => setCurrentPage(currentPage + 1) : undefined}
              >
                ›
              </a>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

// EventCard component similar to ResourceCard
const EventCard = ({ hit }) => {
  return (
    <a href={hit.url} className="flex flex-col justify-between w-auto">
      <span>
        <span className="relative block">
          <GatsbyImage 
            image={getImage(hit.data.meta_image)} 
            alt={hit.data.meta_image?.alt || `${hit.data.resource_title.text} graphic`} 
            className="rounded-t-lg" 
          />
          <div className="bottom-7 left-7 absolute bg-black bg-opacity-60 inline-block pr-1 pl-2 pt-1 pb-1 rounded font-bold uppercase tracking-widest text-vs text-white">
            {hit.data.resource_type}
          </div>
        </span>
        <span className="p-7 block">
          <span className="bg-grey-light py-1 px-2 rounded text-sm font-bold mb-3 inline-block">
            {hit.data.date_published}
          </span>
          <span className="title text-xl font-bold pr-6 block leading-tight">
            {hit.data.resource_title.text}
          </span>
          <p className="mb-0 mt-4">{hit.data.description.text}</p>
        </span>
      </span>
      <span className="pb-7 pr-7 pl-7 block">
        <div className="btn text-sm bg-yellow">Learn more</div>
      </span>
    </a>
  );
};



export const AllEvents = (props) => {
  const sc = props.slice.primary;

  let space = sc.spacing;

  if (space === "Both large") {
    space = "gutter";
  } else if (space === "Both small") {
    space = "gutter sm";
  } else if (space === "Top large") {
    space = "gutter bot";
  } else if (space === "Top small") {
    space = "gutter bot sm";
  } else if (space === "Bottom large") {
    space = "gutter top";
  } else {
    space = "gutter top sm";
  }

  let bg = sc.background === "gradient" ? "gradient-to-b from-grey-light to-white" : sc.background === "grey" ? "grey-light" : sc.background;

  // Get today's timestamp for filtering past events
  var today = new Date();
  today.setHours(0, 0, 0, 0);
  var todayValue = Math.floor(today.valueOf() / 1000); // Convert to seconds to match the date_timestamp format

  console.log(todayValue);

  return (
    <section className={"relative bg-" + bg + " " + space}>
      <div className="wrapper all-resources">
        {sc.section_title?.text ? (
          <div className="mb-0">
            <PrismicRichText field={sc.section_title.richText} />
          </div>
        ) : (
          <h2 className="mb-0">Past events</h2>
        )}
        
        <InstantSearch searchClient={searchClient} indexName="All_events" routing={true}>
          <Configure 
            hitsPerPage={100}
          />
          <PastEventsHits hitComponent={EventCard} />
        </InstantSearch>
      </div>
    </section>
  );
};